import styled from '@emotion/styled/macro';
import { Variables } from '../base/variables';
import { SidePanel } from '../messaging/side-panel';

const V = Variables.ThemeLight;

/**
 * Above MUI's modal layer (1300), as the payer drawer is: at `SidePanel`'s own 1200 an invisible
 * popover backdrop left mounted by the table can sit on top of it, and the drawer renders but
 * cannot be clicked. The flip side is that a MUI dialog opened FROM the drawer would render behind
 * it — which is why "discard changes?" is asked inline in the footer, not in a dialog.
 */
export const Panel = styled(SidePanel)`
    z-index: 1400;
`;

export const Form = styled.form`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${V.Background2};
`;

export const Header = styled.header`
    flex: none;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 20px 14px;
    border-bottom: 1px solid ${V.Border1};
    background: ${V.Background3};
`;

export const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

export const Title = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: ${V.TextStrong};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Subtitle = styled.div`
    margin-top: 4px;
    font-size: 13px;
    color: ${V.TextMild};
`;

export const Body = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 20px 24px;
`;

export const Section = styled.section`
    padding: 16px;
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    background: ${V.Background3};
`;

export const SectionHead = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
`;

export const SectionTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const SectionSubtitle = styled.div`
    margin-top: 2px;
    font-size: 12px;
    color: ${V.TextMild};
`;

export const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 14px;
`;

export const Field = styled.div<{ span?: 1 | 2 }>`
    grid-column: span ${({ span }) => span ?? 1};
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

export const Label = styled.label`
    font-size: 12px;
    font-weight: 500;
    color: ${V.TextMedium};
`;

export const Required = styled.span`
    margin-left: 2px;
    color: ${V.WarningInverted3};
`;

export const Hint = styled.div`
    font-size: 12px;
    color: ${V.TextMild};
`;

export const ToggleRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 6px 0;
`;

export const ToggleText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const ToggleLabel = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${V.TextStrong};
`;

export const Address = styled.div`
    padding: 12px;
    border: 1px solid ${V.Border1};
    border-radius: 6px;
    background: ${V.Background2};

    & + & {
        margin-top: 10px;
    }
`;

export const AddressHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const Empty = styled.div`
    padding: 12px;
    border: 1px dashed ${V.Border1};
    border-radius: 6px;
    font-size: 13px;
    text-align: center;
    color: ${V.TextMild};
`;

export const Footer = styled.footer`
    flex: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 20px;
    border-top: 1px solid ${V.Border1};
    background: ${V.Background3};
`;

export const SaveState = styled.span<{ dirty: boolean }>`
    font-size: 12px;
    color: ${({ dirty }) => (dirty ? V.AttentionInverted3 : V.TextXMild)};
`;

export const FooterActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const Centered = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    font-size: 13px;
    color: ${V.TextMild};
`;

export const ButtonContent = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
`;
